/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Text,
  VStack,
  Switch as ChakraSwitch,
} from '@chakra-ui/react';
import {
  Control,
  Controller,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { Input, Switch } from '@gamma/ui/form-fields';
import { Panel } from '@gamma/ui/layout';
import CheckCircleSharpIcon from '@gamma/ui/icons/CheckCircleSharpIcon';
import VisibilityOffSharpIcon from '@gamma/ui/icons/VisibilityOffSharpIcon';
import VisibilitySharpIcon from '@gamma/ui/icons/VisibilitySharpIcon';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import { EntraIDFormData } from '../../queries';

interface EntraFormLabels {
  receiveEntityEnrichmentEntra: string;
  enabledLabel: string;
  disabledLabel: string;
  connectionsSection: string;
  connectionVerified: string;
  connectionNotVerifiedLabel: string;
  removeConnection: string;
  verifyConnection: string;
  settingsSection: string;
  identityEnrichment: string;
  identityEnrichmentDescription: string;
  responseActions: string;
  responseActionsDescription: string;
  entraFormFields: {
    entra_tenant_id: string;
    entra_client_id: string;
    entra_client_secret: string;
  };
}

interface EntraIDSettingsFormProps {
  control: Control<EntraIDFormData>;
  register: UseFormRegister<EntraIDFormData>;
  formData: EntraIDFormData;
  isValid: boolean;
  appSecretInputPwType: boolean;
  setAppSecretInputPwType: (v: boolean) => void;
  configurationVerified?: boolean;
  verifyLoading: boolean;
  onVerify: () => void;
  onDeleteConnection: () => void;
  setValue: UseFormSetValue<EntraIDFormData>;
  labels: EntraFormLabels;
}

export const renderSettingSwitch = (
  title: string,
  description: string,
  isChecked: boolean,
  isFormEnabled: boolean,
  onChange: (checked: boolean) => void,
): JSX.Element => {
  return (
    <Box px={4} py={3} w="100%">
      <Flex justify="space-between" align="start">
        <Box>
          <Text fontSize="md" color="text.primary">
            {title}
          </Text>
          <Text fontSize="sm" color="text.secondary">
            {description}
          </Text>
        </Box>
        <ChakraSwitch
          colorScheme="green"
          isChecked={isChecked}
          isDisabled={!isFormEnabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </Flex>
    </Box>
  );
};

export const EntraIDSettingsForm = ({
  control,
  register,
  formData,
  isValid = false,
  appSecretInputPwType = true,
  setAppSecretInputPwType = () => {},
  configurationVerified = false,
  verifyLoading = false,
  onVerify = () => {},
  onDeleteConnection = () => {},
  setValue = () => {},
  labels,
}: EntraIDSettingsFormProps): JSX.Element => {
  return (
    <VStack as="form" alignItems="flex-start" spacing={4}>
      <Text textStyle="body-md">{labels.receiveEntityEnrichmentEntra}</Text>

      <Controller
        name="enabled"
        control={control}
        render={({ field: { onChange, value, name } }): JSX.Element => (
          <Switch
            inline
            name={name}
            isChecked={value}
            onChange={onChange}
            label={
              formData.enabled ? labels.enabledLabel : labels.disabledLabel
            }
          />
        )}
      />

      <Input
        {...register('entra_tenant_id', { required: true })}
        isRequired
        label={labels.entraFormFields.entra_tenant_id}
      />

      <Input
        {...register('entra_client_id', { required: true })}
        isRequired
        label={labels.entraFormFields.entra_client_id}
      />

      <Input
        {...register('entra_client_secret', { required: true })}
        isRequired
        label={labels.entraFormFields.entra_client_secret}
        type={appSecretInputPwType ? 'password' : 'text'}
        rightElement={
          <IconButton
            cursor="pointer"
            aria-label={`${appSecretInputPwType ? 'Show' : 'Hide'} secret`}
            onClick={() => setAppSecretInputPwType(!appSecretInputPwType)}
          >
            {appSecretInputPwType ? (
              <VisibilitySharpIcon size="md" />
            ) : (
              <VisibilityOffSharpIcon size="md" />
            )}
          </IconButton>
        }
      />

      {/* Connections */}
      <Box w="100%">
        <Text fontSize="md">{labels.connectionsSection}</Text>

        <Flex justify="space-between" align="center">
          <HStack>
            <Box
              w="8px"
              h="8px"
              borderRadius="full"
              bg={configurationVerified ? 'green.400' : 'orange.400'}
            />
            <Text fontSize="md" color="gray.200">
              {configurationVerified
                ? labels.connectionVerified
                : labels.connectionNotVerifiedLabel}
            </Text>
          </HStack>

          {configurationVerified ? (
            <Button
              colorScheme="gray"
              leftIcon={<DeleteSharpIcon />}
              size="md"
              variant="solid"
              onClick={onDeleteConnection}
              isDisabled={!formData.enabled}
            >
              {labels.removeConnection}
            </Button>
          ) : (
            <Button
              size="md"
              variant="solid"
              colorScheme="gray"
              leftIcon={<CheckCircleSharpIcon />}
              onClick={onVerify}
              isDisabled={!formData.enabled || !isValid}
              isLoading={verifyLoading}
            >
              {labels.verifyConnection}
            </Button>
          )}
        </Flex>
      </Box>

      {/* Settings */}
      <Box pt={2}>
        <Text fontSize="md" mb={2}>
          {labels.settingsSection}
        </Text>

        <Panel layerStyle="second">
          <VStack spacing={0} divider={<Divider borderColor="border.2" />}>
            {renderSettingSwitch(
              labels.identityEnrichment,
              labels.identityEnrichmentDescription,
              formData.identity_enrichment_enabled,
              !!formData?.enabled,
              (checked) =>
                setValue('identity_enrichment_enabled', checked, {
                  shouldDirty: true,
                  shouldValidate: true,
                }),
            )}

            {renderSettingSwitch(
              labels.responseActions,
              labels.responseActionsDescription,
              formData.edr_action_enabled,
              !!formData?.enabled,
              (checked) =>
                setValue('edr_action_enabled', checked, {
                  shouldDirty: true,
                  shouldValidate: true,
                }),
            )}
          </VStack>
        </Panel>
      </Box>
    </VStack>
  );
};
